package dev.danko.roblox_craft_ai.uikit.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input

@Composable
fun AppInput(
    value: String,
    onValueChanged: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    placeholder: String = "",
    type: InputType<String> = InputType.Text,
    error: String? = null,
    helperText: String? = null,
    disabled: Boolean = false,
    leadingIcon: (@Composable () -> Unit)? = null,
    trailingIcon: (@Composable () -> Unit)? = null
) {
    val theme = LocalAppTheme.current
    val palette = theme.colors
    var isFocused by remember { mutableStateOf(false) }

    val hasError = !error.isNullOrBlank()
    val borderColor = when {
        hasError -> palette.statusFailed
        isFocused -> palette.accent
        else -> palette.border
    }
    val shadowSpread = if (isFocused || hasError) 2.px else 0.px
    val shadowColor = if (hasError) palette.statusFailed else palette.accent.toRgb().copyf(alpha = 0.35f)

    Column(modifier = Modifier.fillMaxWidth().then(modifier)) {
        if (!label.isNullOrBlank()) {
            SpanText(
                text = label,
                modifier = Modifier
                    .fontSize(0.85.cssRem)
                    .fontWeight(FontWeight.SemiBold)
                    .color(if (hasError) palette.statusFailed else palette.textPrimary)
                    .margin(bottom = 0.35.cssRem)
            )
        }

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .backgroundColor(palette.surfaceElevated)
                .borderRadius(theme.radii.md)
                .border(width = 1.px, style = LineStyle.Solid, color = borderColor)
                .boxShadow(0.px, 0.px, 0.px, shadowSpread, color = shadowColor)
                .padding(leftRight = 0.75.cssRem, topBottom = 0.5.cssRem)
                .transition(
                    Transition.of("border-color", 150.ms),
                    Transition.of("box-shadow", 150.ms)
                )
                .opacity(if (disabled) 0.5 else 1.0),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (leadingIcon != null) {
                Box(modifier = Modifier.margin(right = 0.5.cssRem)) {
                    leadingIcon()
                }
            }

            Input(
                type = type,
                attrs = Modifier
                    .fillMaxWidth()
                    .backgroundColor(Color.rgba(0, 0, 0, 0f))
                    .color(palette.textPrimary)
                    .fontSize(0.95.cssRem)
                    .outline(width = 0.px, style = LineStyle.None, color = Color.rgba(0, 0, 0, 0f))
                    .border(width = 0.px, style = LineStyle.None, color = Color.rgba(0, 0, 0, 0f))
                    .toAttrs {
                        value(value)
                        placeholder(placeholder)
                        if (disabled) attr("disabled", "")
                        onInput { evt -> onValueChanged(evt.value) }
                        onFocusIn { isFocused = true }
                        onFocusOut { isFocused = false }
                    }
            )

            if (trailingIcon != null) {
                Box(modifier = Modifier.margin(left = 0.5.cssRem)) {
                    trailingIcon()
                }
            }
        }

        if (hasError) {
            SpanText(
                text = error ?: "",
                modifier = Modifier
                    .fontSize(0.75.cssRem)
                    .color(palette.statusFailed)
                    .margin(top = 0.3.cssRem)
            )
        } else if (!helperText.isNullOrBlank()) {
            SpanText(
                text = helperText,
                modifier = Modifier
                    .fontSize(0.75.cssRem)
                    .color(palette.textSecondary)
                    .margin(top = 0.3.cssRem)
            )
        }
    }
}
